// Procedural Staunton-style silhouettes, authored for this project in a
// 100×100 box with the base resting at y = 88. Each piece is one path filled
// with the even-odd rule, so the knight's eye and the bishop's mitre slit are
// holes. Path2D objects are built on first use — module import stays free of
// canvas APIs.
import { BISHOP, KING, KNIGHT, PAWN, QUEEN, ROOK } from '../game/types.js';

export const SHAPE_BOX = 100;

const BASE = 'M 24 78 L 76 78 Q 79 83 76 88 L 24 88 Q 21 83 24 78 Z';

function circle(cx: number, cy: number, r: number): string {
  return `M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy} Z`;
}

const PAWN_PATH = [
  circle(50, 28, 12),
  'M 37 42 L 63 42 L 61 49 L 56 49 Q 57 64 68 76 L 32 76 Q 43 64 44 49 L 39 49 Z',
  BASE,
].join(' ');

const ROOK_PATH = [
  'M 27 20 L 36 20 L 36 28 L 45 28 L 45 20 L 55 20 L 55 28 L 64 28 L 64 20 L 73 20 L 73 38 L 66 43',
  'L 66 69 L 72 76 L 28 76 L 34 69 L 34 43 L 27 38 Z',
  BASE,
].join(' ');

const BISHOP_PATH = [
  circle(50, 13, 5),
  'M 50 19 C 65 29 67 43 59 52 L 41 52 C 33 43 35 29 50 19 Z',
  'M 52 29 L 57 33 L 48 42 L 43 38 Z',
  'M 37 52 L 63 52 L 61 58 L 39 58 Z',
  'M 42 58 L 58 58 Q 58 68 67 76 L 33 76 Q 42 68 42 58 Z',
  BASE,
].join(' ');

const KNIGHT_PATH = [
  'M 66 76 L 30 76 C 30 64 38 56 46 48 C 40 50 34 54 27 53 C 21 51 19 45 23 40',
  'C 31 32 37 24 43 18 L 45 9 L 52 15 C 67 17 75 30 75 46 C 75 58 69 66 66 76 Z',
  circle(45, 28, 2.6),
  BASE,
].join(' ');

const QUEEN_PATH = [
  'M 24 31 L 34 53 L 38 27 L 46 51 L 50 23 L 54 51 L 62 27 L 66 53 L 76 31 L 70 63 L 30 63 Z',
  circle(24, 27, 4.5),
  circle(38, 23, 4.5),
  circle(50, 19, 4.5),
  circle(62, 23, 4.5),
  circle(76, 27, 4.5),
  'M 31 63 L 69 63 L 65 69 L 67 76 L 33 76 L 35 69 Z',
  BASE,
].join(' ');

const KING_PATH = [
  'M 47 6 L 53 6 L 53 12 L 59 12 L 59 18 L 53 18 L 53 27 L 47 27 L 47 18 L 41 18 L 41 12 L 47 12 Z',
  'M 50 27 C 70 27 77 40 66 56 L 34 56 C 23 40 30 27 50 27 Z',
  'M 32 56 L 68 56 L 66 62 L 34 62 Z',
  'M 36 62 L 64 62 Q 64 70 70 76 L 30 76 Q 36 70 36 62 Z',
  BASE,
].join(' ');

const PATH_DATA: Readonly<Record<number, string>> = {
  [PAWN]: PAWN_PATH,
  [KNIGHT]: KNIGHT_PATH,
  [BISHOP]: BISHOP_PATH,
  [ROOK]: ROOK_PATH,
  [QUEEN]: QUEEN_PATH,
  [KING]: KING_PATH,
};

const cache = new Map<number, Path2D>();

/** The silhouette for a piece type (1–6). */
export function pieceShape(type: number): Path2D {
  const cached = cache.get(type);
  if (cached !== undefined) return cached;
  const path = new Path2D(PATH_DATA[type] ?? PAWN_PATH);
  cache.set(type, path);
  return path;
}
